
import React from "react";
import { NoseurObject, useSearchParams } from "@rivtn/noseur";
import { i18nManager } from "../i18n";
import PageHead from "./common/PageHead";
import Pagination from "./common/Pagination";
import FilterChips from "./common/FilterChips";
import TournamentCard from "./common/TournamentCard";
import { Utils } from "../../../../utils";
import styles from "../assets/style.module.css";
import { ApiResponseData, BaseService, ITeamNotification, ITeamWebsite, ITournamentEdition, OmneHostService, SanitizedResponse } from "../../../../services";

const SECTION_TYPES: NoseurObject<string[]> = {
    members: ["MEMBER_JOINED", "MEMBER_LEFT", "TEAM_MEMBER_KICKED", "TEAM_DISBANDED"],
    timeline: ["CREATE_TEAM", "TEAM_DISBANDED", "TEAM_REACTIVATED", "TEAM_OWNERSHIP_CHANGED"],
    tournament_timeline: ["TEAM_TOURNAMENT_EDITION_REGISTRATION", "TEAM_TOURNAMENT_EDITION_WITHDRAWAL", "TEAM_TOURNAMENT_EDITION_REMOVAL", "TEAM_OWNERSHIP_CHANGED"],
};

interface TournamentListProps {
    title: string;
    subtitle: string;
    page: string;
    muted?: boolean;
    data?: ApiResponseData<ITournamentEdition>;
    onPageChange: (page: string) => void;
}

function TournamentList({ title, subtitle, page, muted, data, onPageChange }: TournamentListProps) {
    return (<div className={styles["block"]}>
        <h2 className={styles["h1"]} style={muted ? { color: "var(--muted)" } : undefined}>{title}</h2>
        <p className={styles["lead"]}>{subtitle}</p>
        {(!!data?.content)
            ? (data.content.length > 0
                ? (<div className={styles["t-grid"]}>
                    {data.content.map((t) => (<TournamentCard key={t.external_id} tournament={t} />))}
                </div>)
                : Utils.emptyState({ margin: "4rem" }))
            : Utils.loadingState()}
        <Pagination page={parseInt(page)} rowsPerPage={data?.size} totalRecords={data?.total_elements} onPageChange={(p) => onPageChange(`${p}`)} style={{ justifyContent: "flex-start" }} />
    </div>);
}

export interface ActivitiesProps {
    website: ITeamWebsite;
}

function Activities({ website }: ActivitiesProps) {

    const labels = i18nManager.Labels.activities;
    const omnehostService = OmneHostService.getInstance();
    const [pastTournaments, setPastTournaments] = React.useState<ApiResponseData<ITournamentEdition>>();
    const [activeTournaments, setActiveTournaments] = React.useState<ApiResponseData<ITournamentEdition>>();
    const [notifications, setNotifications] = React.useState<ApiResponseData<ITeamNotification>>();
    const { page, setPage, atpage, setAtpage, ptpage, setPtpage, section, setSection } = useSearchParams({ defaultInit: window.location.search, params: { page: "1", atpage: "1", ptpage: "1", section: "tournaments" } });

    React.useEffect(fetchNotifications, [omnehostService, page, section]);
    React.useEffect(() => fetchTournaments("PAST"), [ptpage]);
    React.useEffect(() => fetchTournaments("ACTIVE"), [atpage]);

    return (<>
        <PageHead kicker={labels.kicker} title={labels.title} subtitle={labels.desc} />
        <section className={styles["section-page"]}>
            <div className={styles["container"]}>
                <FilterChips value={`${section}`} onChange={setSection} options={[
                    ["tournaments", labels.tournaments],
                    ["tournament_timeline", labels.tournament_timeline],
                    ["timeline", labels.timeline],
                    ["members", labels.members],
                    ["achievements", labels.achievements],
                ]} />

                {section === "tournaments" ? (<>
                    <TournamentList title={labels.active} subtitle={labels.active_desc} page={`${atpage}`} data={activeTournaments} onPageChange={setAtpage} />
                    <TournamentList muted title={labels.past} subtitle={labels.past_desc} page={`${ptpage}`} data={pastTournaments} onPageChange={setPtpage} />
                </>) : (<>
                    {(!!notifications?.content)
                        ? (notifications.content.length > 0
                            ? (<div className={styles["feed"]}>
                                {notifications.content.map((a) => (<div key={a.external_id} className={styles["feed-item"]}>
                                    {a.image_location ? (<img className={styles["feed-icon"]} alt="" src={a.image_location} />) : (<span className={styles["feed-icon"]} />)}
                                    <div className={styles["feed-text"]} dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(a.message) }} />
                                    <span className={styles["feed-time"]}>{Utils.fineDate(new Date(a.created_at))}</span>
                                </div>))}
                            </div>)
                            : Utils.emptyState())
                        : Utils.loadingState()}
                    <Pagination page={parseInt(`${page}`)} rowsPerPage={notifications?.size} totalRecords={notifications?.total_elements} onPageChange={(p) => setPage(`${p}`)} />
                </>)}
            </div>
        </section>
    </>);

    function fetchNotifications() {
        setNotifications({});
        if (section === "tournaments") return;
        omnehostService.queryTeamNotifications(website.team.public_id, {
            page,
            sort: "created_at,DESC",
            type: SECTION_TYPES[`${section}`] ?? ["NONE"],
        }).then(({ apiResponse }: SanitizedResponse<ITeamNotification>) => {
            setNotifications(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function fetchTournaments(status: "PAST" | "ACTIVE") {
        omnehostService.queryTeamTournaments(website.team.public_id, {
            status,
            page: status === "PAST" ? ptpage : atpage,
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEdition>) => {
            (status === "PAST" ? setPastTournaments : setActiveTournaments)(apiResponse.data!);
        }).catch(BaseService.reportError);
    }
}

export default Activities;
